import React, { useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react'

import Avatar from '@mui/material/Avatar'
import SmartToy from '@mui/icons-material/SmartToy'
import PhotoCameraOutlined from '@mui/icons-material/PhotoCameraOutlined'
import FiberManualRecordOutlined from '@mui/icons-material/FiberManualRecordOutlined'
import CloseIcon from '@mui/icons-material/Close'
import ContentCopyOutlined from '@mui/icons-material/ContentCopyOutlined'
import CheckOutlined from '@mui/icons-material/CheckOutlined'
import ArrowForward from '@mui/icons-material/ArrowForward'
import { Play, Check, X as XIcon, Minus } from 'lucide-react'

import { EditorView, basicSetup } from 'codemirror'
import { EditorState } from '@codemirror/state'
import { keymap } from '@codemirror/view'
import { indentWithTab } from '@codemirror/commands'
import { indentUnit } from '@codemirror/language'
import { html as htmlLanguage } from '@codemirror/lang-html'
import { css as cssLanguage } from '@codemirror/lang-css'
import { javascript as jsLanguage } from '@codemirror/lang-javascript'
import { pythonLanguage } from '@codemirror/lang-python'

import { getGuiSessionConfig } from '../../../workspace/config/guiSessionConfig'
import { evaluateWebDevChecks } from '../../../workspace/utils/webDevChecks'
import { formatTestsForRender } from '../../../workspace/utils/TestCases/testDataFormatter'
import { buildContextualPrompt } from '../../../workspace/utils/AIChat/promptContextBuilder'
import { prepareOpenAIMessages } from '../../../workspace/utils/AIChat/memoryManagement'
import { AI_AGENT_ID } from '../../../workspace/service/workspaceMessageService'
import { postStudentChat } from '../../../../service/studentChatService'
import {
  NavbarReplica,
  PreviewHeader,
  RightPanelTabs,
  parseFiles,
  ALL_WEB_DEV_FILES,
  WEB_DEV_FILE_LABELS,
  WELCOME_TEXT,
  LEFT_WIDTH,
  TERMINAL_HEIGHT,
  IDE_SPLIT_PERCENT,
  JS_VIEW_SPLIT_PERCENT,
} from '../WorkspacePreview'
import { regionAttrs } from './previewRegions'
import { runPythonPreview } from './pythonRunner'
import { StudentTestCell } from '../../TestCaseIO'
import { completeTestResults } from '../../testResults'
import defaultAvatar from '../../../../icon/defaultAvatars/user.png'

import '../../../workspace/css/workspace.scss'
import '../../../../css/studentview-workspace.scss'
import '../../../../css/livepreview-workspace.scss'

// The creation wizard's live, interactive copy of a student's /workspace/:id (Workspace.jsx)
// for every session the workspace runs:
//   - GUI (web-dev): HTML, CSS, JavaScript, Mixed, JSBin, InitialFinal, InitialFinalCSS and
//     InitialFinalJS. Edit the files, Render the page and click around in it, Run Tests.
//   - The Python family (Python, Vizmental): Workspace's non-GUI tree - task and test
//     cases on the left, one Python editor seeded from the starter template, the terminal,
//     and the AI chat column only when chat is on. Run executes in Pyodide (pythonRunner).
// In both the instructor can drag the panel dividers and chat with the AI assistant.
//
// It reproduces Workspace's render tree (same layout per guiConfig.layout, same ws-*
// classes, markup copied from the live leaves) but reads no app context and persists
// nothing: no code backup, no run/submission rows, no chat messages, no telemetry, no
// rrweb, no screenshots. The only network traffic is the AI chat turn, sent through the
// same postStudentChat route students use (the server applies the session's AI Chat
// Policy), and only once the session row exists (plus Pyodide's own runtime download).

// ChatContext.js's stripSurroundingCodeFence (not exported there), verbatim: InitialFinal*
// replies are meant to be pasted whole, so a fenced block is unwrapped.
const stripSurroundingCodeFence = (text) => {
  const wrapped = /^```[a-zA-Z]*[ \t]*\r?\n([\s\S]*?)\r?\n?```\s*$/.exec(text.trim())
  if (wrapped) return wrapped[1]
  const anyFence = /```[a-zA-Z]*[ \t]*\r?\n([\s\S]*?)\r?\n?```/.exec(text)
  return anyFence ? anyFence[1] : text
}
const FENCE_STRIPPED_LANGUAGES = ['html-target', 'css-target', 'js-target']

// Stand-in for currentUser in the chat (ChatWindow tells the student's own messages apart
// by sender id; the name falls back to "Student" exactly like Workspace does).
const PREVIEW_STUDENT_ID = 'lpw-preview-student'
const CHARACTER_LIMIT = 500
const NO_SESSION_CHAT = 'The AI assistant works once the session is created, after the Language step.'
const NO_CAPTURE_TITLE = 'Screenshots are not taken in this preview'

const welcomeMessage = () => ({
  id: 'welcome-msg',
  content: WELCOME_TEXT,
  sender_id: AI_AGENT_ID,
  timestamp: new Date().toISOString(),
})

const formatTime = (isoString) => {
  if (!isoString) return ''
  return new Date(isoString).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })
}

const clamp = (value, min, max) => Math.max(min, Math.min(max, value))

// TerminalContext.runTestsPython's terminal lines for one run: each printed line, the error
// (if any), then - with tests on and a non-empty test list - the unrounded pass rate over
// the whole list (its reportTestResults).
const workspaceTerminalLines = (stdout, error, results) => {
  const printed = String(stdout || '').split('\n')
  if (printed[printed.length - 1] === '') printed.pop()
  const lines = printed.map((content) => ({ type: 'stdout', content }))
  if (error) lines.push({ type: 'stderr', content: error })
  if (results && results.length > 0) {
    const passed = results.filter((r) => r === true).length
    lines.push({ type: 'test_result', content: `Tests Completed: ${(passed / results.length) * 100}%` })
  }
  return lines
}

// ---------------------------------------------------------------------------------------
// Rendered page. Same document LivePreview.jsx builds (base reset, CSP, student css/html,
// js in a try/catch) minus its telemetry/snapshot instrumentation. The iframe is sandboxed
// allow-scripts only - no allow-modals - so alert/confirm/prompt are replaced by an in-page
// box rather than silently doing nothing (the Mixed/JSBin starter's button alerts).
// ---------------------------------------------------------------------------------------
const DIALOG_SHIM = `(function () {
  function show(message) {
    try {
      var box = document.createElement('div');
      box.setAttribute('role', 'alertdialog');
      box.setAttribute('style', 'position:fixed;top:12px;left:50%;transform:translateX(-50%);z-index:2147483647;' +
        'width:min(400px,calc(100vw - 24px));box-sizing:border-box;background:#fff;color:#171717;border:1px solid #d1d5db;' +
        'border-radius:8px;box-shadow:0 8px 24px rgba(0,0,0,.18);padding:14px 16px;text-align:left;' +
        'font:14px/1.45 -apple-system,BlinkMacSystemFont,"Segoe UI",Roboto,sans-serif;');
      var head = document.createElement('div');
      head.textContent = 'This page says';
      head.setAttribute('style', 'font-weight:600;margin:0 0 6px;');
      var text = document.createElement('div');
      text.textContent = message === undefined ? '' : String(message);
      text.setAttribute('style', 'white-space:pre-wrap;word-break:break-word;');
      var ok = document.createElement('button');
      ok.type = 'button';
      ok.textContent = 'OK';
      ok.setAttribute('style', 'display:block;margin:12px 0 0 auto;padding:5px 16px;border:none;border-radius:6px;' +
        'background:#2563eb;color:#fff;font:600 13px -apple-system,BlinkMacSystemFont,"Segoe UI",Roboto,sans-serif;cursor:pointer;');
      ok.onclick = function () { if (box.parentNode) box.parentNode.removeChild(box); };
      box.appendChild(head);
      box.appendChild(text);
      box.appendChild(ok);
      (document.body || document.documentElement).appendChild(box);
    } catch (e) {}
  }
  window.alert = function (message) { show(message); };
  window.confirm = function (message) { show(message); return true; };
  window.prompt = function (message, value) { show(message); return value === undefined ? '' : String(value); };
})();`

// Without allow-forms the browser never fires 'submit' (the sandbox stops form submission
// before the event), so a student's form.addEventListener('submit', ...) would never run.
// The real pane has allow-forms plus CSP form-action 'none': the submit event fires and
// any navigation is blocked. This reproduces that inside the page: a submit button click
// (or requestSubmit()) that nothing cancelled dispatches a cancelable submit event, and
// nothing navigates. The listeners live on the iframe's own window/document and die with
// the iframe; nothing is registered on the instructor's page. If a browser does fire the
// native event, the synthetic one is skipped.
const FORM_SUBMIT_SHIM = `(function () {
  var nativeSubmitFor = null;
  document.addEventListener('submit', function (e) { if (!e.lpwSynthetic) nativeSubmitFor = e.target; }, true);
  function fireSubmit(form, submitter) {
    nativeSubmitFor = null;
    setTimeout(function () {
      if (nativeSubmitFor === form) return;
      var ev;
      try { ev = new SubmitEvent('submit', { bubbles: true, cancelable: true, submitter: submitter || null }); }
      catch (err) { ev = new Event('submit', { bubbles: true, cancelable: true }); }
      ev.lpwSynthetic = true;
      form.dispatchEvent(ev);
    }, 0);
  }
  function valid(form, submitter) {
    if (form.noValidate || (submitter && submitter.formNoValidate)) return true;
    if (form.checkValidity && !form.checkValidity()) { if (form.reportValidity) form.reportValidity(); return false; }
    return true;
  }
  window.addEventListener('click', function (e) {
    if (e.defaultPrevented || !e.target || !e.target.closest) return;
    var b = e.target.closest('button, input');
    if (!b || !b.form || b.disabled) return;
    var type = (b.getAttribute('type') || (b.tagName === 'BUTTON' ? 'submit' : '')).toLowerCase();
    if (type !== 'submit' && type !== 'image') return;
    if (valid(b.form, b)) fireSubmit(b.form, b);
  });
  if (window.HTMLFormElement && HTMLFormElement.prototype.requestSubmit) {
    HTMLFormElement.prototype.requestSubmit = function (submitter) {
      if (valid(this, submitter)) fireSubmit(this, submitter);
    };
  }
})();`

const buildPreviewDoc = ({ html = '', css = '', js = '' }) => `
<!DOCTYPE html>
<html lang="en">
<head>
  <meta charset="UTF-8">
  <meta name="viewport" content="width=device-width, initial-scale=1.0">
  <meta http-equiv="Content-Security-Policy" content="default-src 'none'; script-src 'unsafe-inline'; style-src 'unsafe-inline'; img-src https: data:; form-action 'none';">
  <title>Live Preview</title>
  <script>${DIALOG_SHIM}</script>
  <script>${FORM_SUBMIT_SHIM}</script>
  <style>
    html, body {
      margin: 0;
      padding: 16px;
      font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif;
      background-color: #ffffff;
      color: #171717;
    }
    ${css}
  </style>
</head>
<body>
  ${html}
  <script>
    try {
      ${js}
    } catch (err) {}
  </script>
</body>
</html>
`

// LivePreview.jsx, interactive. `files` null => not rendered yet (manual preview mode,
// "Select Render ... to preview your page."). Like the real pane, a new document is shown
// 400ms after the rendered files change, and an unchanged render keeps the page as is.
function LivePane({
  files,
  title = 'Web Preview',
  badge = 'Interactive',
  showRefresh = true,
  chromeStyle = false,
  renderLabel = null,
  onRender,
  disableInteraction = false,
  region = null,
}) {
  const doc = useMemo(() => (files ? buildPreviewDoc(files) : ''), [files])
  const [shownDoc, setShownDoc] = useState('')
  const [frameKey, setFrameKey] = useState(0)

  useEffect(() => {
    if (!doc) {
      setShownDoc('')
      return undefined
    }
    const timeout = setTimeout(() => setShownDoc(doc), 400)
    return () => clearTimeout(timeout)
  }, [doc])

  const hasRendered = Boolean(files)

  return (
    <div
      {...(region || {})}
      className="ws-chat-container"
      style={{ display: 'flex', flexDirection: 'column', height: '100%', borderLeft: '1px solid #e5e5e5' }}
    >
      <PreviewHeader
        title={title}
        badge={badge}
        showRefresh={showRefresh}
        chromeStyle={chromeStyle}
        renderLabel={onRender ? renderLabel : null}
        onRender={onRender}
        onRefresh={() => setFrameKey((key) => key + 1)}
      />
      <div style={{ flex: 1, position: 'relative', background: '#ffffff', display: 'flex', justifyContent: 'center' }}>
        {shownDoc ? (
          <iframe
            key={frameKey}
            srcDoc={shownDoc}
            title="VizGroup Live Web Preview"
            sandbox="allow-scripts"
            style={{
              width: '100%',
              height: '100%',
              border: 'none',
              background: '#ffffff',
              pointerEvents: disableInteraction ? 'none' : 'auto',
            }}
          />
        ) : (
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100%', color: '#737373', fontSize: '0.875rem' }}>
            {hasRendered ? 'Loading preview...' : `Select ${renderLabel || 'Render'} to preview your page.`}
          </div>
        )}
      </div>
    </div>
  )
}

// ---------------------------------------------------------------------------------------
// CodeEditor.jsx, interactive. Same CodeMirror setup and theme as the student editor; each
// pane reads its content once at mount, and the parent remounts panes (key) to load new
// content (starter re-seed, Reset Template, preview reset).
// ---------------------------------------------------------------------------------------
const EDITOR_THEME = EditorView.theme({
  '&': { height: '100%', fontSize: '14px', color: '#1f2937' },
  '.cm-content': { padding: '16px', minHeight: '100%', color: '#1f2937', fontFamily: 'Monaco, Menlo, monospace' },
  '.cm-focused': { outline: 'none' },
  '.cm-editor': { height: '100%', minHeight: '0px', backgroundColor: '#ffffff' },
  '.cm-scroller': { height: '100%', minHeight: '0px', overflow: 'auto' },
  '.cm-line': { color: '#1f2937' },
  '.cm-cursor': { borderLeftColor: '#1f2937' },
  '.cm-selectionBackground': { backgroundColor: '#e5e7eb !important' },
  '.cm-keyword': { color: '#7c3aed' },
  '.cm-string': { color: '#059669' },
  '.cm-comment': { color: '#6b7280' },
  '.cm-number': { color: '#dc2626' },
})

// 'python' is the non-GUI editor's single file (CodeEditor's LANGUAGE_EXTENSIONS.python).
const languageFor = (file) => {
  if (file === 'python') return pythonLanguage
  if (file === 'css') return cssLanguage()
  if (file === 'js') return jsLanguage()
  return htmlLanguage()
}

function CodePane({ file, initialValue, editable, onChange, style }) {
  const hostRef = useRef(null)
  const onChangeRef = useRef(onChange)
  onChangeRef.current = onChange
  const mountProps = useRef({ file, initialValue, editable })

  useEffect(() => {
    const { file: paneFile, initialValue: doc, editable: canEdit } = mountProps.current
    const view = new EditorView({
      state: EditorState.create({
        doc: doc || '',
        extensions: [
          basicSetup,
          languageFor(paneFile),
          EditorState.readOnly.of(!canEdit),
          EditorView.editable.of(canEdit),
          indentUnit.of('    '),
          keymap.of([indentWithTab]),
          EditorView.updateListener.of((update) => {
            if (update.docChanged) onChangeRef.current?.(paneFile, update.state.doc.toString())
          }),
          EDITOR_THEME,
        ],
      }),
      parent: hostRef.current,
    })
    return () => view.destroy()
  }, [])

  return <div ref={hostRef} style={style} />
}

const RESET_CONFIRM = 'Are you sure you want to reset the editor to the default starter template? This will overwrite your current code.'

const hoverBackground = (over, out) => ({
  onMouseEnter: (e) => { e.currentTarget.style.backgroundColor = over },
  onMouseLeave: (e) => { e.currentTarget.style.backgroundColor = out },
})

function ResetTemplateButton({ onReset, columns = false }) {
  return (
    <button
      type="button"
      onClick={onReset}
      style={{
        padding: '4px 8px',
        fontSize: '0.725rem',
        fontWeight: 600,
        border: '1px solid #d1d5db',
        borderRadius: '4px',
        backgroundColor: '#ffffff',
        color: '#374151',
        cursor: 'pointer',
        outline: 'none',
        ...(columns ? {} : { marginRight: '8px', marginBottom: '4px' }),
        transition: 'all 0.15s ease'
      }}
      {...hoverBackground('#f3f4f6', '#ffffff')}
    >
      Reset Template
    </button>
  )
}

// Tabbed mode (HTML/CSS/JavaScript/Mixed, and the InitialFinal/InitialFinalCSS editors).
function TabbedEditor({ getFile, seedKey, enabledFiles, hideDisabledTabs = false, extraReadOnlyFiles = [], paneTitle = null, onFileChange, onReset }) {
  const [activeTab, setActiveTab] = useState(enabledFiles[0] || 'html')
  const tabs = hideDisabledTabs
    ? ALL_WEB_DEV_FILES.filter((f) => enabledFiles.includes(f) || extraReadOnlyFiles.includes(f))
    : ALL_WEB_DEV_FILES
  const current = tabs.includes(activeTab) ? activeTab : (enabledFiles[0] || 'html')

  return (
    <div className="ws-editor-container" style={{ display: 'flex', flexDirection: 'column', height: '100%', minHeight: 0 }}>
      <div className="ws-editor-tabs" style={{
        display: 'flex',
        background: '#fafafa',
        borderBottom: '1px solid #e5e5e5',
        padding: '6px 8px 0 8px',
        gap: '4px',
        justifyContent: 'space-between',
        alignItems: 'center'
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
          {paneTitle && (
            <span style={{ fontSize: '0.95rem', fontWeight: 700, color: '#171717', paddingBottom: '6px', whiteSpace: 'nowrap' }}>
              {paneTitle}
            </span>
          )}
          <div style={{ display: 'flex', gap: '4px' }}>
            {tabs.map((tab) => {
              const readOnly = !enabledFiles.includes(tab)
              return (
                <button
                  key={tab}
                  type="button"
                  onClick={() => setActiveTab(tab)}
                  title={readOnly ? `${WEB_DEV_FILE_LABELS[tab]} is read-only` : undefined}
                  style={{
                    padding: '6px 14px',
                    fontSize: '0.8rem',
                    fontWeight: 600,
                    border: '1px solid #e5e5e5',
                    borderBottom: tab === current ? '1px solid #ffffff' : 'none',
                    borderRadius: '6px 6px 0 0',
                    backgroundColor: tab === current ? '#ffffff' : '#fafafa',
                    color: tab === current ? (readOnly ? '#9ca3af' : '#171717') : '#737373',
                    cursor: 'pointer',
                    marginBottom: '-1px',
                    zIndex: tab === current ? 2 : 1,
                    textTransform: 'uppercase',
                    outline: 'none',
                    transition: 'all 0.15s ease',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '4px'
                  }}
                >
                  {WEB_DEV_FILE_LABELS[tab]}
                  {readOnly && <span aria-hidden="true" style={{ fontSize: '0.7rem' }}>🔒</span>}
                </button>
              )
            })}
          </div>
        </div>
        <ResetTemplateButton onReset={onReset} />
      </div>
      <CodePane
        key={`${seedKey}-${current}`}
        file={current}
        initialValue={getFile(current)}
        editable={enabledFiles.includes(current)}
        onChange={onFileChange}
        style={{ width: '100%', flex: 1, minHeight: 0 }}
      />
    </div>
  )
}

// Columns mode: JSBin's side-by-side panes with show/hide toggles, or InitialFinalJS's
// always-visible stacked panes.
function ColumnsEditor({ getFile, seedKey, enabledFiles, stacked = false, paneTitle = null, onFileChange, onReset, region = null }) {
  const [visiblePanes, setVisiblePanes] = useState({ html: true, css: true, js: true })
  const visibleCount = ALL_WEB_DEV_FILES.filter((f) => visiblePanes[f]).length
  const togglePane = (file) => {
    if (visiblePanes[file] && visibleCount <= 1) return
    setVisiblePanes((prev) => ({ ...prev, [file]: !prev[file] }))
  }

  return (
    <div {...(region || {})} className="ws-editor-container ws-editor-columns" style={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
      <div className="ws-editor-tabs" style={{
        display: 'flex',
        background: '#fafafa',
        borderBottom: '1px solid #e5e5e5',
        padding: '6px 8px',
        justifyContent: 'space-between',
        alignItems: 'center',
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
          {stacked && paneTitle && (
            <span style={{ fontSize: '0.95rem', fontWeight: 700, color: '#171717', whiteSpace: 'nowrap' }}>
              {paneTitle}
            </span>
          )}
          {!stacked && (
            <div style={{ display: 'flex', gap: '4px' }}>
              {ALL_WEB_DEV_FILES.map((file) => {
                const isVisible = visiblePanes[file]
                return (
                  <button
                    key={file}
                    type="button"
                    onClick={() => togglePane(file)}
                    title={isVisible ? `Hide ${WEB_DEV_FILE_LABELS[file]}` : `Show ${WEB_DEV_FILE_LABELS[file]}`}
                    style={{
                      padding: '5px 12px',
                      fontSize: '0.75rem',
                      fontWeight: 600,
                      border: '1px solid #d1d5db',
                      borderRadius: '4px',
                      backgroundColor: isVisible ? '#171717' : '#ffffff',
                      color: isVisible ? '#ffffff' : '#6b7280',
                      cursor: 'pointer',
                      outline: 'none',
                      textTransform: 'uppercase',
                      letterSpacing: '0.03em',
                      transition: 'all 0.15s ease'
                    }}
                  >
                    {WEB_DEV_FILE_LABELS[file]}
                  </button>
                )
              })}
            </div>
          )}
        </div>
        <ResetTemplateButton onReset={onReset} columns />
      </div>
      <div style={{ display: 'flex', flexDirection: stacked ? 'column' : 'row', flex: 1, minHeight: 0 }}>
        {ALL_WEB_DEV_FILES.map((file) => {
          const isVisible = stacked ? true : visiblePanes[file]
          const isFirstVisible = isVisible && ALL_WEB_DEV_FILES.slice(0, ALL_WEB_DEV_FILES.indexOf(file)).every((f) => stacked || !visiblePanes[f])
          const enabled = enabledFiles.includes(file)
          return (
            <div
              key={`${file}-pane-wrapper`}
              style={{
                display: isVisible ? 'flex' : 'none',
                flex: isVisible ? 1 : '0 0 0px',
                minWidth: stacked ? undefined : 0,
                minHeight: stacked ? 0 : undefined,
                flexDirection: 'column',
                borderLeft: !stacked && !isFirstVisible ? '1px solid #e5e5e5' : 'none',
                borderTop: stacked && !isFirstVisible ? '1px solid #e5e5e5' : 'none',
              }}
            >
              <div style={{
                padding: '6px 12px',
                fontSize: '0.75rem',
                fontWeight: 700,
                letterSpacing: '0.03em',
                textTransform: 'uppercase',
                color: enabled ? '#171717' : '#9ca3af',
                background: '#fafafa',
                borderBottom: '1px solid #e5e5e5',
              }}>
                {WEB_DEV_FILE_LABELS[file]}
                {!enabled && <span aria-hidden="true" style={{ marginLeft: '4px', fontSize: '0.7rem' }}>🔒</span>}
              </div>
              <div style={{ flex: 1, minHeight: 0 }}>
                <CodePane
                  key={`${seedKey}-${file}`}
                  file={file}
                  initialValue={getFile(file)}
                  editable={enabled}
                  onChange={onFileChange}
                  style={{ width: '100%', height: '100%' }}
                />
              </div>
            </div>
          )
        })}
      </div>
    </div>
  )
}

// ---------------------------------------------------------------------------------------
// TaskDescription.jsx / TestCases.jsx / Terminal.jsx markup, fed from local state.
// ---------------------------------------------------------------------------------------
function TaskSection({ task, region }) {
  return (
    <div {...region} className="ws-content-section">
      <h3 className="ws-section-title">Task Description</h3>
      <div className="ws-description-box">{task}</div>
    </div>
  )
}

const PassIcon = ({ result }) => {
  if (result === null) return <div className="ws-test-icon ws-test-icon-pending"><Minus className="ws-icon-md" /></div>
  if (result) return <div className="ws-test-icon ws-test-icon-passed"><Check className="ws-icon-md" /></div>
  return <div className="ws-test-icon ws-test-icon-failed"><XIcon className="ws-icon-md" /></div>
}

function TestCasesSection({ tests, region }) {
  return (
    <div {...region} className="ws-content-section">
      <h3 className="ws-section-title">Test Cases</h3>
      <div className="ws-test-table-wrapper">
        <table className="ws-test-table">
          <thead>
            <tr>
              <th>Name</th>
              <th className="ws-result-column">Result</th>
            </tr>
          </thead>
          <tbody>
            {tests.map((test, index) => (
              <tr key={index}>
                {/* test_list rows carry `view` (formatTestsForRender): public ones show their
                    input and expected output. Web-dev checks are names only. */}
                <td>{test.view ? <StudentTestCell view={test.view} /> : test.testName}</td>
                <td className="ws-result-column">
                  <PassIcon result={test.result} />
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  )
}

function TerminalPanel({ height, onRun, isRunning = false, runLabel = 'Run Tests', output, region, onResizeStart }) {
  const scrollRef = useRef(null)
  useEffect(() => {
    if (scrollRef.current) scrollRef.current.scrollTop = scrollRef.current.scrollHeight
  }, [output])

  return (
    <>
      {/* position/zIndex restate .ws-terminal-panel's own values inline so the region
          highlight (position: relative) can never pull the panel out of its overlay slot. */}
      <div {...region} className="ws-terminal-panel" style={{ height: `${height}px`, position: 'absolute', zIndex: 10 }}>
        <div className="ws-terminal-controls">
          <div className="ws-controls-left">
            {/* The web checks run synchronously, so for GUI sessions the real button's brief
                'Running...' state never shows here; Python runs show it. */}
            <button type="button" className="ws-run-button" onClick={onRun} disabled={isRunning}>
              <Play className="ws-icon-sm" />
              {isRunning ? 'Running...' : runLabel}
            </button>
          </div>
        </div>
        <div className="ws-terminal-output" ref={scrollRef}>
          <div className="ws-output-content">
            {output.length === 0 && `Click '${runLabel}' to execute your code...`}
            {output.map((line, index) => (
              <div key={index} className={`terminal-line ${line.type}`}>
                {line.content}
              </div>
            ))}
          </div>
        </div>
      </div>
      <div
        className="ws-resizer ws-vertical"
        style={{ position: 'absolute', bottom: height, left: 0, right: 0, zIndex: 30, touchAction: 'none' }}
        onPointerDown={onResizeStart}
      />
    </>
  )
}

// ---------------------------------------------------------------------------------------
// ChatWindow.jsx, interactive. Typing, Enter-to-send, the 500-character limit, the
// Thinking indicator and per-reply Copy work as in the real window. The screenshot /
// record buttons are drawn (Workspace shows them for every GUI session) but disabled:
// the preview never captures the screen.
// ---------------------------------------------------------------------------------------
function ChatPanel({ messages, onSendMessage, isAiProcessing, captureButtons, disabledReason, error, onDismissError, region }) {
  const [chatInput, setChatInput] = useState('')
  const [copiedMessageId, setCopiedMessageId] = useState(null)
  const chatHistoryRef = useRef(null)
  const copyTimerRef = useRef(null)

  useEffect(() => {
    if (chatHistoryRef.current) chatHistoryRef.current.scrollTop = chatHistoryRef.current.scrollHeight
  }, [messages, isAiProcessing])

  useEffect(() => () => clearTimeout(copyTimerRef.current), [])

  const blocked = Boolean(disabledReason)
  const handleSubmit = (e) => {
    e.preventDefault()
    // Same guard as ChatWindow.handleSubmit: Enter sends even past the 500-character limit
    // (only the Send button is disabled there).
    if (blocked || !chatInput.trim() || isAiProcessing) return
    onSendMessage(chatInput)
    setChatInput('')
  }

  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) handleSubmit(e)
  }

  const handleCopyMessage = (msg) => {
    if (!navigator.clipboard?.writeText) return
    navigator.clipboard.writeText(msg.content).then(() => {
      setCopiedMessageId(msg.id)
      clearTimeout(copyTimerRef.current)
      copyTimerRef.current = setTimeout(() => setCopiedMessageId((current) => (current === msg.id ? null : current)), 1500)
    }).catch(() => {})
  }

  const sendDisabled = blocked || isAiProcessing || !chatInput.trim() || chatInput.length > CHARACTER_LIMIT
  const captureDisabledStyle = {
    background: 'none', border: 'none', cursor: 'default',
    color: '#4b5563', opacity: 0.4,
    display: 'flex', alignItems: 'center', padding: '6px',
  }

  return (
    <div {...region} className="ws-chat-container">
      <div className="ws-chat">
        <div className="ws-chat-history" ref={chatHistoryRef}>
          {messages.map((msg, index) => {
            const isOwner = msg.sender_id === PREVIEW_STUDENT_ID
            const isAI = msg.sender_id === AI_AGENT_ID
            return (
              <div key={msg.id || index} className={`ws-message ${isOwner ? 'ws-owner' : ''}`}>
                <div className="ws-message-info">
                  {isAI ? (
                    <Avatar sx={{ bgcolor: '#1976d2', width: 38, height: 38 }}>
                      <SmartToy sx={{ fontSize: '1rem' }} />
                    </Avatar>
                  ) : (
                    <img src={defaultAvatar} alt="user avatar" />
                  )}
                  <span className="ws-info-name">{isAI ? 'AI Assistant' : 'Student'}</span>
                  <span className="ws-info-time">{formatTime(msg.timestamp)}</span>
                </div>
                <div className="ws-message-content-wrapper">
                  <div className="ws-message-content">
                    <pre style={{ whiteSpace: 'pre-wrap', fontFamily: 'inherit' }}>{msg.content}</pre>
                  </div>
                  {isAI && (
                    <button
                      type="button"
                      onClick={() => handleCopyMessage(msg)}
                      title="Copy this response"
                      style={{
                        display: 'flex', alignItems: 'center', gap: '4px', marginTop: '4px',
                        padding: '3px 8px', fontSize: '0.72rem', fontWeight: 600,
                        border: '1px solid #e5e5e5', borderRadius: '4px', background: '#ffffff',
                        color: copiedMessageId === msg.id ? '#059669' : '#4b5563', cursor: 'pointer',
                      }}
                    >
                      {copiedMessageId === msg.id ? (
                        <><CheckOutlined style={{ fontSize: '0.85rem' }} /> Copied</>
                      ) : (
                        <><ContentCopyOutlined style={{ fontSize: '0.85rem' }} /> Copy</>
                      )}
                    </button>
                  )}
                </div>
              </div>
            )
          })}

          {isAiProcessing && (
            <div className="ws-message">
              <div className="ws-message-info">
                <Avatar sx={{ bgcolor: '#1976d2', width: 38, height: 38 }}>
                  <SmartToy sx={{ fontSize: '1rem' }} />
                </Avatar>
                <span className="ws-info-name">AI Assistant</span>
              </div>
              <div className="ws-message-content-wrapper">
                <div className="ws-message-content" style={{ color: '#888', fontStyle: 'italic', marginTop: '28px' }}>
                  Thinking...
                </div>
              </div>
            </div>
          )}
        </div>

        {/* ChatWindow's attachment/notice strip. The real one shows capture errors here;
            the preview uses the same red box for a failed chat turn. */}
        <div style={{ padding: '0 12px', background: '#ffffff' }}>
          {disabledReason && <div className="lpw-chat-note">{disabledReason}</div>}
          {error && (
            <div style={{
              display: 'flex', alignItems: 'center', gap: '8px',
              padding: '6px 8px', margin: '8px 0 0', borderRadius: '6px',
              background: '#fef2f2', border: '1px solid #fecaca',
            }}>
              <span style={{ fontSize: '0.78rem', color: '#991b1b', flex: 1 }}>{error}</span>
              <button
                type="button"
                onClick={onDismissError}
                title="Dismiss"
                style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#991b1b', display: 'flex', padding: '2px' }}
              >
                <CloseIcon style={{ fontSize: '1rem' }} />
              </button>
            </div>
          )}
        </div>

        <form className="ws-message-form" onSubmit={handleSubmit}>
          {captureButtons !== 'none' && (
            <div style={{ display: 'flex', gap: '2px' }}>
              <button type="button" disabled title={NO_CAPTURE_TITLE} style={captureDisabledStyle}>
                <PhotoCameraOutlined style={{ fontSize: '1.15rem' }} />
              </button>
              {captureButtons === 'camera+record' && (
                <button type="button" disabled title={NO_CAPTURE_TITLE} style={captureDisabledStyle}>
                  <FiberManualRecordOutlined style={{ fontSize: '1.15rem' }} />
                </button>
              )}
            </div>
          )}
          <textarea
            className="ws-message-input"
            placeholder={isAiProcessing ? 'AI is typing...' : 'Ask a question...'}
            rows={1}
            value={chatInput}
            onChange={(e) => setChatInput(e.target.value)}
            onKeyDown={handleKeyDown}
            disabled={isAiProcessing || blocked}
          />
          <div className="ws-char-count">
            <span style={{
              color: chatInput.length > CHARACTER_LIMIT ? '#d32f2f' : '#999',
              fontWeight: chatInput.length > CHARACTER_LIMIT ? 'bold' : 'normal'
            }}>
              {chatInput.length}/{CHARACTER_LIMIT}
            </span>
          </div>
          <button type="submit" className="ws-send-button" disabled={sendDisabled} style={{ opacity: sendDisabled ? 0.5 : 1 }}>
            Send
          </button>
        </form>
      </div>
    </div>
  )
}

// ---------------------------------------------------------------------------------------
// The workspace.
// ---------------------------------------------------------------------------------------
export default function InteractiveWorkspacePreview({
  session,
  testsEnabled = true,
  chatEnabled = true,
  highlight = [],
  resetToken,
  onBeforeChat,
}) {
  const type = session?.type
  const task = session?.task
  const testCode = session?.test_code ?? null
  const guiConfig = useMemo(() => getGuiSessionConfig({ type, task, test_code: testCode }), [type, task, testCode])
  const starterCode = guiConfig?.starterCode || ''
  const starterFiles = useMemo(() => parseFiles(starterCode), [starterCode])
  const configType = guiConfig?.type || null
  // Non-GUI (Python family): one Python file, seeded from the session's starter template.
  const codeStarter = guiConfig ? '' : (testCode?.template || '')
  const testList = useMemo(() => (Array.isArray(session?.test_list) ? session.test_list : []), [session?.test_list])
  const regions = Array.isArray(highlight) ? highlight : []
  const region = (id) => regionAttrs(id, regions)

  // --- student code: a ref (the editors are uncontrolled), re-seeded per the state rules
  const filesRef = useRef(null)
  if (filesRef.current === null) filesRef.current = { ...starterFiles }
  const editedRef = useRef(false)
  const [seedVersion, setSeedVersion] = useState(0)
  const [resetVersion, setResetVersion] = useState(0)
  const getFile = (file) => filesRef.current[file] || ''
  const handleFileChange = (file, value) => {
    filesRef.current = { ...filesRef.current, [file]: value }
    editedRef.current = true
  }
  const codeRef = useRef(null)
  if (codeRef.current === null) codeRef.current = codeStarter
  const handleCodeChange = (file, value) => {
    codeRef.current = value
    editedRef.current = true
  }

  // --- rendered preview (manual mode: nothing until Render)
  const [renderedFiles, setRenderedFiles] = useState(null)

  // --- terminal / tests
  const [terminalOutput, setTerminalOutput] = useState([])
  const [testResults, setTestResults] = useState([])
  // Python runs are async: a ref guards double clicks (the click lands before the re-render
  // that disables the button), and the generation drops runs started before a reset or a
  // test change.
  const [isRunning, setIsRunning] = useState(false)
  const runningRef = useRef(false)
  const runGenerationRef = useRef(0)
  const mountedRef = useRef(true)
  useEffect(() => {
    mountedRef.current = true
    return () => {
      mountedRef.current = false
      runGenerationRef.current += 1
    }
  }, [])

  // --- chat
  const [messages, setMessages] = useState(() => [welcomeMessage()])
  const [isAiProcessing, setIsAiProcessing] = useState(false)
  const [chatError, setChatError] = useState(null)
  const chatGenerationRef = useRef(0)
  useEffect(() => () => { chatGenerationRef.current += 1 }, [])

  // --- layout
  const [rightPanelTab, setRightPanelTab] = useState('preview')
  const [taskCollapsed, setTaskCollapsed] = useState(false)
  const [sizes, setSizes] = useState({ left: null, right: null, terminal: TERMINAL_HEIGHT, ideSplit: IDE_SPLIT_PERCENT, jsViewSplit: JS_VIEW_SPLIT_PERCENT })
  const [activeResizer, setActiveResizer] = useState(null)
  const rootRef = useRef(null)
  const contentRef = useRef(null)
  const ideSplitRowRef = useRef(null)
  const jsViewSplitColRef = useRef(null)
  const [rootWidth, setRootWidth] = useState(0)

  useLayoutEffect(() => {
    const el = rootRef.current
    if (!el) return undefined
    const update = () => setRootWidth(el.clientWidth)
    update()
    if (typeof ResizeObserver === 'undefined') return undefined
    const observer = new ResizeObserver(update)
    observer.observe(el)
    return () => observer.disconnect()
  }, [])

  // Re-seed / reset. resetToken (and a change of exercise type) starts the student over;
  // a new starter only replaces code the instructor has not touched in the preview.
  const seedRef = useRef({ starterCode, codeStarter, configType, resetToken })
  useEffect(() => {
    const prev = seedRef.current
    seedRef.current = { starterCode, codeStarter, configType, resetToken }
    if (prev.resetToken !== resetToken || prev.configType !== configType) {
      filesRef.current = { ...starterFiles }
      codeRef.current = codeStarter
      editedRef.current = false
      chatGenerationRef.current += 1
      runGenerationRef.current += 1
      setSeedVersion((v) => v + 1)
      setResetVersion((v) => v + 1)
      setRenderedFiles(null)
      setTerminalOutput([])
      setTestResults([])
      setMessages([welcomeMessage()])
      setIsAiProcessing(false)
      setChatError(null)
      setRightPanelTab('preview')
      setTaskCollapsed(false)
      setSizes({ left: null, right: null, terminal: TERMINAL_HEIGHT, ideSplit: IDE_SPLIT_PERCENT, jsViewSplit: JS_VIEW_SPLIT_PERCENT })
      return
    }
    if (prev.starterCode !== starterCode && !editedRef.current) {
      filesRef.current = { ...starterFiles }
      setSeedVersion((v) => v + 1)
      // An already-rendered page follows the untouched code it was rendered from.
      setRenderedFiles((rendered) => (rendered ? { ...starterFiles } : rendered))
    }
    // The Python editor follows the drafted template the same way.
    if (prev.codeStarter !== codeStarter && !editedRef.current) {
      codeRef.current = codeStarter
      setSeedVersion((v) => v + 1)
    }
  }, [starterCode, starterFiles, codeStarter, configType, resetToken])

  // Edited checks invalidate the last run's pass/fail column (it was for other tests). For
  // Python that is the test list, the starter/harness and the tests switch: the wizard's
  // Testcases step feeds draft rows before the harness is regenerated.
  const checksKey = useMemo(() => (guiConfig
    ? JSON.stringify([guiConfig.checks || null, guiConfig.tests || null])
    : JSON.stringify([testsEnabled, testList, testCode?.starter || '', testCode?.unit_test || ''])
  ), [guiConfig, testsEnabled, testList, testCode])
  const checksKeyRef = useRef(checksKey)
  useEffect(() => {
    if (checksKeyRef.current === checksKey) return
    checksKeyRef.current = checksKey
    runGenerationRef.current += 1
    setTestResults([])
    setTerminalOutput([])
  }, [checksKey])

  // Workspace: chat turned off while on the AI tab falls back to the preview tab.
  useEffect(() => {
    if (!chatEnabled && rightPanelTab === 'ai') setRightPanelTab('preview')
  }, [chatEnabled, rightPanelTab])

  // Guide attention: when the step being configured is the chat, bring the AI tab forward
  // (in the tabbed layouts the chat is otherwise behind "Live Preview"), and put the
  // preview back afterwards unless the instructor switched tabs themselves meanwhile.
  const wantsChat = regions.includes('chat')
  const autoSwitchedRef = useRef(false)
  const rightPanelTabRef = useRef(rightPanelTab)
  rightPanelTabRef.current = rightPanelTab
  useEffect(() => {
    if (wantsChat && chatEnabled) {
      if (rightPanelTabRef.current !== 'ai') {
        autoSwitchedRef.current = true
        setRightPanelTab('ai')
      }
    } else if (!wantsChat && autoSwitchedRef.current) {
      autoSwitchedRef.current = false
      setRightPanelTab('preview')
    }
  }, [wantsChat, chatEnabled, resetVersion])
  const selectRightTab = (tab) => {
    autoSwitchedRef.current = false
    setRightPanelTab(tab)
  }

  // Bring a highlighted section of the scrolling left pane (e.g. the test cases under a
  // long task) into view. Only that pane is scrolled - never the wizard page around it.
  const highlightKey = regions.join(',')
  useLayoutEffect(() => {
    const active = rootRef.current?.querySelector('.ws-panel-content [data-lp-active="true"]')
    const pane = active?.closest('.ws-panel-content')
    if (!active || !pane) return
    const paneRect = pane.getBoundingClientRect()
    const rect = active.getBoundingClientRect()
    // Rects are in (FitViewport-scaled) screen pixels; scrollTop is in layout pixels.
    const scale = pane.offsetHeight ? paneRect.height / pane.offsetHeight : 1
    if (!scale) return
    if (rect.top < paneRect.top || rect.bottom > paneRect.bottom) {
      pane.scrollTop += (rect.top - paneRect.top) / scale - 12
    }
  }, [highlightKey, configType])

  // Panel dividers (usePanelResize), driven by pointer capture on the divider itself so no
  // window/document listener is ever registered. Pixel deltas are divided by the
  // FitViewport scale; the percentage splits are scale-free.
  const startResize = (kind, containerRef = null) => (e) => {
    if (e.button !== 0) return
    e.preventDefault()
    const handle = e.currentTarget
    try { handle.setPointerCapture(e.pointerId) } catch (err) { /* old browsers: drag still works while over the handle */ }
    setActiveResizer(kind)
    const onMove = (ev) => {
      const content = contentRef.current
      if (!content) return
      const rect = content.getBoundingClientRect()
      const scale = content.offsetWidth ? rect.width / content.offsetWidth : 1
      if (kind === 'left') {
        const width = clamp((ev.clientX - rect.left) / scale, 200, 800)
        setSizes((s) => ({ ...s, left: width }))
      } else if (kind === 'right') {
        const width = clamp((rect.right - ev.clientX) / scale, 200, 800)
        setSizes((s) => ({ ...s, right: width }))
      } else if (kind === 'terminal') {
        const height = clamp((rect.bottom - ev.clientY) / scale, 100, 600)
        setSizes((s) => ({ ...s, terminal: height }))
      } else if (kind === 'ideSplit' && containerRef?.current) {
        const box = containerRef.current.getBoundingClientRect()
        if (box.width) setSizes((s) => ({ ...s, ideSplit: clamp(((ev.clientX - box.left) / box.width) * 100, 20, 80) }))
      } else if (kind === 'jsViewSplit' && containerRef?.current) {
        const box = containerRef.current.getBoundingClientRect()
        if (box.height) setSizes((s) => ({ ...s, jsViewSplit: clamp(((ev.clientY - box.top) / box.height) * 100, 20, 80) }))
      }
    }
    const onEnd = () => {
      handle.removeEventListener('pointermove', onMove)
      handle.removeEventListener('pointerup', onEnd)
      handle.removeEventListener('pointercancel', onEnd)
      handle.removeEventListener('lostpointercapture', onEnd)
      setActiveResizer(null)
    }
    handle.addEventListener('pointermove', onMove)
    handle.addEventListener('pointerup', onEnd)
    handle.addEventListener('pointercancel', onEnd)
    handle.addEventListener('lostpointercapture', onEnd)
  }

  const root = (children) => (
    <div className="svw-root lpw-root" ref={rootRef}>
      {children}
    </div>
  )

  // usePanelResize's 450px columns; below ~1500px of preview width they narrow (never
  // under 300px) so the middle column keeps a usable editor.
  const autoPanel = rootWidth ? clamp(Math.round(rootWidth * 0.3), 300, LEFT_WIDTH) : LEFT_WIDTH
  const leftWidth = sizes.left ?? autoPanel
  const rightWidth = sizes.right ?? autoPanel

  // ChatContext.sendMessage, minus every write: same prompt (buildContextualPrompt with
  // the current code, test results and terminal), same history window and system prompt
  // (prepareOpenAIMessages), same route. GUI sends the files as JSON with language =
  // guiConfig.phase; Python sends the editor text with language 'python'.
  const sendMessage = async (userText) => {
    if (!userText.trim() || isAiProcessing || !session?.id) return
    const generation = chatGenerationRef.current
    setIsAiProcessing(true)
    setChatError(null)

    const newUserMsg = {
      id: Date.now(),
      content: userText,
      sender_id: PREVIEW_STUDENT_ID,
      timestamp: new Date().toISOString(),
    }
    const updatedMessages = [...messages, newUserMsg]
    setMessages(updatedMessages)

    // Snapshot at send time, like ChatContext (it reads codeRef/test results synchronously).
    const promptSession = { ...session, enable_tests: testsEnabled }
    const fullPrompt = buildContextualPrompt(
      userText,
      guiConfig ? JSON.stringify(filesRef.current) : codeRef.current,
      testResults,
      terminalOutput,
      guiConfig ? session.task || guiConfig.assignment : session.task,
      promptSession,
      '',
      null
    )
    const language = guiConfig ? guiConfig.phase : 'python'
    const apiPayload = prepareOpenAIMessages(updatedMessages, fullPrompt, language, undefined)

    let stage = 'policy'
    try {
      if (onBeforeChat) await onBeforeChat()
      if (generation !== chatGenerationRef.current) return
      stage = 'send'
      const reply = await postStudentChat(session, apiPayload)
      if (generation !== chatGenerationRef.current) return
      const content = typeof reply?.content === 'string' ? reply.content : ''
      const aiContent = FENCE_STRIPPED_LANGUAGES.includes(language) ? stripSurroundingCodeFence(content) : content
      setMessages((prev) => [...prev, {
        id: Date.now() + 1,
        content: aiContent,
        sender_id: AI_AGENT_ID,
        timestamp: new Date().toISOString(),
      }])
    } catch (err) {
      if (generation !== chatGenerationRef.current) return
      setChatError(stage === 'policy'
        ? 'Could not save the AI chat policy, so nothing was sent. Try again.'
        : err?.response?.data?.message || 'The AI assistant did not answer. Try again.')
    } finally {
      if (generation === chatGenerationRef.current) setIsAiProcessing(false)
    }
  }

  const chatPanel = (captureButtons) => (
    <ChatPanel
      key={`chat-${resetVersion}`}
      messages={messages}
      onSendMessage={sendMessage}
      isAiProcessing={isAiProcessing}
      captureButtons={captureButtons}
      disabledReason={session?.id ? null : NO_SESSION_CHAT}
      error={chatError}
      onDismissError={() => setChatError(null)}
      region={region('chat')}
    />
  )

  if (!guiConfig) {
    // TerminalContext.runTestsPython, minus every write: starter, code, harness in that
    // order. With tests on, every test ends pass or fail, even after an error.
    const runPython = async () => {
      if (runningRef.current) return
      runningRef.current = true
      const generation = runGenerationRef.current
      const withTests = testsEnabled
      const testCount = testList.length
      setIsRunning(true)
      setTerminalOutput([])
      let lines
      let results = null
      try {
        const run = await runPythonPreview({
          code: codeRef.current || '',
          starter: testCode?.starter || '',
          harness: testCode?.unit_test || '',
          withTests,
          order: 'workspace',
          trimError: false,
          // /workspace's terminal never shows stderr (warnings, sys.stderr writes).
          captureStderr: false,
        })
        if (withTests) results = completeTestResults(run.results, testCount)
        lines = workspaceTerminalLines(run.stdout, run.error, results)
      } catch (err) {
        // The runner itself failed (e.g. Pyodide could not be downloaded).
        if (withTests) results = completeTestResults([], testCount)
        lines = workspaceTerminalLines('', err?.message || String(err), results)
      } finally {
        runningRef.current = false
        if (mountedRef.current) setIsRunning(false)
      }
      if (!mountedRef.current || generation !== runGenerationRef.current) return
      setTerminalOutput(lines)
      if (results) setTestResults(results)
    }

    return root(
      <>
        <NavbarReplica />
        <div className="svw-page-body">
          <div className="ws-container">
            <div className="ws-content" ref={contentRef}>

              {/* LEFT PANEL: Task Description + Test Cases (only with tests on). */}
              <div className="ws-panel-left" style={{ width: `${leftWidth}px` }}>
                <div className="ws-panel-content">
                  <TaskSection task={session?.task} region={region('task')} />
                  {testsEnabled && (
                    <>
                      <div className="ws-section-divider"></div>
                      <TestCasesSection tests={formatTestsForRender(session, testResults)} region={region('tests')} />
                    </>
                  )}
                </div>
              </div>
              <div className="ws-resizer ws-horizontal" style={{ touchAction: 'none' }} onPointerDown={startResize('left')} />

              {/* MIDDLE PANEL: CodeEditor's non-web mode (no tabs, no Reset Template) and the
                  terminal overlaying its lower part. */}
              <div className="ws-middle-panel">
                <div className="ws-editor-container" style={{ display: 'flex', flexDirection: 'column', height: '100%', minHeight: 0 }}>
                  <CodePane
                    key={`python-${seedVersion}-${resetVersion}`}
                    file="python"
                    initialValue={codeRef.current}
                    editable
                    onChange={handleCodeChange}
                    style={{ width: '100%', flex: 1, minHeight: 0 }}
                  />
                </div>
                <div {...region('editor')} className="lpw-editor-region" style={{ bottom: `${sizes.terminal}px` }} />
                <TerminalPanel
                  height={sizes.terminal}
                  onRun={runPython}
                  isRunning={isRunning}
                  runLabel={testsEnabled ? 'Run Tests' : 'Run'}
                  output={terminalOutput}
                  region={region('run')}
                  onResizeStart={startResize('terminal')}
                />
              </div>

              {/* RIGHT PANEL: exists only for the AI chat (no Workspace View tabs, no
                  screenshot/record buttons outside GUI sessions). */}
              {chatEnabled && (
                <>
                  <div className="ws-resizer ws-horizontal" style={{ touchAction: 'none' }} onPointerDown={startResize('right')} />
                  <div className="ws-panel-right" style={{ width: `${rightWidth}px`, display: 'flex', flexDirection: 'column' }}>
                    {chatPanel('none')}
                  </div>
                </>
              )}
            </div>
          </div>
        </div>
      </>
    )
  }

  const layout = guiConfig.layout
  const isColumnsLayout = layout === 'columns'
  const isCompareLayout = layout === 'compare'
  const isCompareImageLayout = layout === 'compare-image'
  const isCompareJsLayout = layout === 'compare-js'
  const isAnyCompare = isCompareLayout || isCompareImageLayout || isCompareJsLayout
  const hidesGradingPanels = isColumnsLayout || isAnyCompare
  const taskText = task || guiConfig.assignment
  const enabledFiles = guiConfig.enabledFiles || ALL_WEB_DEV_FILES
  const renderLabel = guiConfig.previewMode === 'manual' ? guiConfig.previewLabel : null
  const dragging = Boolean(activeResizer)

  const testsToRender = (guiConfig.tests || []).map((testName, index) => ({
    testName,
    result: testResults?.[index] ?? null,
  }))

  // Reference renders (compare layouts): memo-free objects are fine here because LivePane
  // keys its document on the built string, not on object identity.
  const initialRefFiles = { html: starterFiles.html, css: starterFiles.css, js: '' }
  const targetRefFiles = { html: guiConfig.finalHtml || '', css: starterFiles.css, js: '' }
  const targetJsFiles = { html: starterFiles.html, css: starterFiles.css, js: guiConfig.finalJs || '' }

  const renderPreview = () => setRenderedFiles({ ...filesRef.current })

  const runTests = () => {
    const { logs, results } = evaluateWebDevChecks(guiConfig, JSON.stringify(filesRef.current))
    setTerminalOutput(logs)
    if (results.length) setTestResults(results)
  }

  const resetTemplate = () => {
    if (!window.confirm(RESET_CONFIRM)) return
    filesRef.current = { ...starterFiles }
    editedRef.current = false
    setSeedVersion((v) => v + 1)
  }

  const editorKey = `${configType}-${resetVersion}`
  const editorProps = {
    getFile,
    seedKey: seedVersion,
    enabledFiles,
    onFileChange: handleFileChange,
    onReset: resetTemplate,
  }

  return root(
    <>
      <NavbarReplica />
      <div className="svw-page-body">
        <div className="ws-container">
          <div className="ws-content" ref={contentRef}>

            {/* LEFT PANEL - graded types: Task Description + Test Cases */}
            {!hidesGradingPanels && (
              <>
                <div className="ws-panel-left" style={{ width: `${leftWidth}px` }}>
                  <div className="ws-panel-content">
                    <TaskSection task={taskText} region={region('task')} />
                    {testsEnabled && (
                      <>
                        <div className="ws-section-divider"></div>
                        <TestCasesSection tests={testsToRender} region={region('tests')} />
                      </>
                    )}
                  </div>
                </div>
                <div className="ws-resizer ws-horizontal" style={{ touchAction: 'none' }} onPointerDown={startResize('left')} />
              </>
            )}

            {/* LEFT PANEL - InitialFinalJS: stacked "Your Code" editor */}
            {isCompareJsLayout && (
              <>
                <div {...region('editor')} className="ws-panel-left" style={{ width: `${leftWidth}px`, display: 'flex', flexDirection: 'column' }}>
                  <ColumnsEditor key={editorKey} {...editorProps} stacked paneTitle="Your Code" />
                </div>
                <div className="ws-resizer ws-horizontal" style={{ touchAction: 'none' }} onPointerDown={startResize('left')} />
              </>
            )}

            {/* MIDDLE PANEL */}
            <div className="ws-middle-panel">
              {isCompareLayout && (
                <>
                  <div {...region('task')} style={{ padding: '12px 16px 0' }}>
                    <TaskSection task={taskText} />
                  </div>
                  <div style={{ display: 'flex', alignItems: 'stretch', gap: '1px', backgroundColor: '#e5e5e5', borderTop: '1px solid #e5e5e5', borderBottom: '1px solid #e5e5e5', height: '38%', minHeight: '160px' }}>
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <LivePane title="Initial" badge={null} showRefresh={false} files={initialRefFiles} disableInteraction={dragging} />
                    </div>
                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', width: '32px', flexShrink: 0, backgroundColor: '#ffffff' }}>
                      <ArrowForward style={{ color: '#9ca3af', fontSize: '1.5rem' }} />
                    </div>
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <LivePane title="Target" badge={null} showRefresh={false} files={targetRefFiles} disableInteraction={dragging} />
                    </div>
                  </div>
                </>
              )}

              {isCompareJsLayout && (
                <div style={{ display: 'flex', flexDirection: 'column', flex: 1, minHeight: 0, overflowY: 'auto' }}>
                  <div {...region('task')} style={{ borderBottom: '1px solid #e5e5e5', flexShrink: 0 }}>
                    <div style={{ display: 'flex', justifyContent: 'flex-end', padding: '6px 16px 0' }}>
                      <button
                        type="button"
                        onClick={() => setTaskCollapsed((collapsed) => !collapsed)}
                        style={{
                          background: 'none', border: 'none', cursor: 'pointer', padding: '2px 4px',
                          fontSize: '0.75rem', fontWeight: 600, color: '#2563eb',
                        }}
                      >
                        {taskCollapsed ? 'Show task ▾' : 'Hide task ▴'}
                      </button>
                    </div>
                    {!taskCollapsed && (
                      <div style={{ padding: '0 16px 12px' }}>
                        <TaskSection task={taskText} />
                      </div>
                    )}
                  </div>
                  <div ref={jsViewSplitColRef} style={{ display: 'flex', flexDirection: 'column', flex: '1 0 480px' }}>
                    <div style={{ height: `${sizes.jsViewSplit}%`, minHeight: 0, borderBottom: '1px solid #e5e5e5' }}>
                      <LivePane title="Target" badge="Interactive" showRefresh={false} chromeStyle files={targetJsFiles} disableInteraction={dragging} />
                    </div>
                    <div
                      onPointerDown={startResize('jsViewSplit', jsViewSplitColRef)}
                      title="Drag to resize"
                      style={{
                        height: '6px', flexShrink: 0, cursor: 'row-resize', touchAction: 'none',
                        backgroundColor: activeResizer === 'jsViewSplit' ? '#2563eb' : '#e5e5e5',
                      }}
                    />
                    <div style={{ height: `${100 - sizes.jsViewSplit}%`, minHeight: 0 }}>
                      <LivePane
                        title="Your View"
                        badge="Interactive"
                        chromeStyle
                        files={renderedFiles}
                        renderLabel={renderLabel}
                        onRender={renderLabel ? renderPreview : undefined}
                        disableInteraction={dragging}
                        region={region('run')}
                      />
                    </div>
                  </div>
                </div>
              )}

              {isCompareImageLayout && (
                <div style={{ display: 'flex', alignItems: 'stretch', gap: '1px', backgroundColor: '#e5e5e5', borderBottom: '1px solid #e5e5e5', height: '38%', minHeight: '200px' }}>
                  <div {...region('task')} style={{ flex: 1, minWidth: 0, padding: '12px 16px', overflow: 'auto', backgroundColor: '#ffffff' }}>
                    <TaskSection task={taskText} />
                  </div>
                  <div style={{ flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column' }}>
                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '10px 16px', borderBottom: '1px solid #e5e5e5', backgroundColor: '#ffffff' }}>
                      <span style={{ fontFamily: "'Open Sans', sans-serif", fontWeight: 600, fontSize: '0.9rem', color: '#171717' }}>Target</span>
                    </div>
                    <div style={{ flex: 1, overflow: 'auto', display: 'flex', alignItems: 'center', justifyContent: 'center', backgroundColor: '#f9fafb' }}>
                      {guiConfig.finalImage ? (
                        <img
                          src={guiConfig.finalImage}
                          alt="Target layout the student is trying to reach"
                          style={{ maxWidth: '100%', maxHeight: '100%', objectFit: 'contain' }}
                        />
                      ) : (
                        <div style={{ color: '#9ca3af', fontSize: '0.85rem', padding: '16px', textAlign: 'center' }}>
                          No target image set. Ask your instructor to add one in the exercise editor.
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              )}

              {isCompareLayout ? (
                <div style={{ display: 'flex', flex: 1, minHeight: 0 }}>
                  <div {...region('editor')} style={{ flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column' }}>
                    <TabbedEditor key={editorKey} {...editorProps} hideDisabledTabs paneTitle="Your Code" />
                  </div>
                  <div style={{ flex: 1, minWidth: 0, borderLeft: '1px solid #e5e5e5' }}>
                    <LivePane
                      files={renderedFiles}
                      renderLabel={renderLabel}
                      onRender={renderLabel ? renderPreview : undefined}
                      disableInteraction={dragging}
                      region={region('run')}
                    />
                  </div>
                </div>
              ) : isCompareImageLayout ? (
                <div ref={ideSplitRowRef} style={{ display: 'flex', flex: 1, minHeight: 0 }}>
                  <div {...region('editor')} style={{ width: `${sizes.ideSplit}%`, minWidth: 0, display: 'flex', flexDirection: 'column' }}>
                    <TabbedEditor key={editorKey} {...editorProps} hideDisabledTabs extraReadOnlyFiles={['html']} paneTitle="Your CSS" />
                  </div>
                  <div
                    onPointerDown={startResize('ideSplit', ideSplitRowRef)}
                    title="Drag to resize"
                    style={{
                      width: '6px', flexShrink: 0, cursor: 'col-resize', touchAction: 'none',
                      backgroundColor: activeResizer === 'ideSplit' ? '#2563eb' : '#e5e5e5',
                    }}
                  />
                  <div style={{ width: `${100 - sizes.ideSplit}%`, minWidth: 0, display: 'flex', flexDirection: 'column' }}>
                    <LivePane
                      title="Your View"
                      badge="Drag divider to resize"
                      showRefresh={false}
                      files={renderedFiles}
                      renderLabel={renderLabel}
                      onRender={renderLabel ? renderPreview : undefined}
                      disableInteraction={dragging}
                      region={region('run')}
                    />
                  </div>
                </div>
              ) : isCompareJsLayout ? null : isColumnsLayout ? (
                <ColumnsEditor key={editorKey} {...editorProps} region={region('editor')} />
              ) : (
                <>
                  <TabbedEditor key={editorKey} {...editorProps} />
                  {/* The editor fills the whole column and the terminal overlays its lower
                      part (as on the real page), so the editor's highlight is drawn by this
                      click-through box over just its visible area. */}
                  <div {...region('editor')} className="lpw-editor-region" style={{ bottom: `${sizes.terminal}px` }} />
                </>
              )}

              {!hidesGradingPanels && (
                <TerminalPanel
                  height={sizes.terminal}
                  onRun={runTests}
                  output={terminalOutput}
                  region={region('run')}
                  onResizeStart={startResize('terminal')}
                />
              )}
            </div>

            <div className="ws-resizer ws-horizontal" style={{ touchAction: 'none' }} onPointerDown={startResize('right')} />

            {isAnyCompare ? (
              // Chat-only column; with chat off the real Workspace leaves it empty.
              <div className="ws-panel-right" style={{ width: `${rightWidth}px`, display: 'flex', flexDirection: 'column' }}>
                {chatEnabled && (
                  <div style={{ flex: 1, minHeight: 0 }}>
                    {chatPanel('camera')}
                  </div>
                )}
              </div>
            ) : (
              <div className="ws-panel-right" style={{ width: `${rightWidth}px`, display: 'flex', flexDirection: 'column' }}>
                {chatEnabled && (
                  <RightPanelTabs previewTabLabel={guiConfig.previewTabLabel} activeTab={rightPanelTab} onSelect={selectRightTab} />
                )}
                {/* Both stay mounted (display toggled) so the rendered page survives a visit
                    to the AI tab, as in Workspace. */}
                {chatEnabled && (
                  <div style={{ display: rightPanelTab === 'ai' ? 'contents' : 'none' }}>
                    {chatPanel('camera+record')}
                  </div>
                )}
                <div style={{ display: !chatEnabled || rightPanelTab === 'preview' ? 'contents' : 'none' }}>
                  {/* JSBin has no terminal: its Output pane (Run with JS) is where code runs. */}
                  <LivePane
                    title="Web Preview"
                    files={renderedFiles}
                    renderLabel={renderLabel}
                    onRender={renderLabel ? renderPreview : undefined}
                    disableInteraction={dragging}
                    region={isColumnsLayout ? region('run') : null}
                  />
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </>
  )
}
